import {Injectable, NgZone} from '@angular/core';
import {Maintenance, MaintenanceProgress, MaintenanceStatus} from "../models/maintenance";
import {BehaviorSubject} from "rxjs";
import {UiManagerService} from "./ui-manager.service";
import {Utils} from "../utils";
import {environment} from "@environments/environment";
import {RestApiManager} from "./rest-api-manager";
import {Paginator} from "../models/paginator";
import {CalendarEvent, EventType} from "../models/calendar-event";
import {Assistance} from "../models/assistance";
import {DeviceType, VideocallProvider, VideocallStatus} from "../models/videocall/videocall-status";
import {AccountManagerService} from "./account-manager-service";
import {PhysicalAssetManagerService} from "./physical-asset-manager.service";
import {EventManagerService} from "./event-manager.service";
import {PersonnelManagerService} from "./personnel-manager.service";
import {
  MaintenanceFieldWorkerRecommendation,
  MaintenanceRecommendations,
  MaintenanceRemoteExpertRecommendation
} from '../models/maintenance-recommendation';
import {Personnel} from "../models/personnel";
import {TimelineElement, TimelineElementStatus, TimelineGroup} from "../components/timeline/timeline.component";
import {AuthoringManagerService} from "./authoring-manager.service";
import {Step, StepExecution} from "../models/step";
import {Task} from '../models/task';
import {PhysicalAsset} from "../models/physical-asset";
import {TrackingManagerService} from "./tracking-manager.service";
import {TranslatePipe} from "@ngx-translate/core";
import {Resource} from "../models/resource";
import {DEBUG, DebugService} from "./debug.service";


@Injectable({
  providedIn: 'root'
})
export class MaintenanceManagerService {

  public openVideoCall: Function;
  public sendVideoCallCard: Function;
  public closeChat: Function;

  private liveFetcher: any;
  private liveMaintenances: BehaviorSubject<Maintenance[]>;

  private showedNotifications: string[] = [];

  constructor(
    private accountManager: AccountManagerService,
    private uiManager: UiManagerService,
    private restApiManager: RestApiManager,
    private physicalAssetManagerService: PhysicalAssetManagerService,
    private eventManagerService: EventManagerService,
    private personnelManagerService: PersonnelManagerService,
    private authoringManagerService: AuthoringManagerService,
    private debugService: DebugService,
    private trackingManagerService: TrackingManagerService,
    private translatePipe: TranslatePipe,
    private ngZone: NgZone,
  ) { }

  init() {
    this.getLiveMaintenances();
  }

  dispose() {
    clearInterval(this.liveFetcher);
    this.liveMaintenances = null;
    this.showedNotifications = [];
  }

  createMaintenance(maintenance: Maintenance): Promise<Maintenance> {
    return this.restApiManager.post<Maintenance>(
      `${environment.restApi.endpoint}/maintenances`,
      maintenance,
      this.accountManager.account.authToken
    );
  }

  async getMaintenances(maintenanceStatus: number[], eventType: number[], fieldServiceIds: number[], paginator?: Paginator<Maintenance>): Promise<Maintenance[]> {
    return this.restApiManager.getAll<Maintenance>(
      Maintenance,
      `${environment.restApi.endpoint}/maintenances/search`,
      this.accountManager.account.authToken,
      {
        paginator,
        params: {
          status: maintenanceStatus,
          eventType: eventType,
          fieldServiceId: fieldServiceIds,
          companyId: this.accountManager.account.company.id
        }
      }
    );
  }

  async getMaintenancesForPhysicalAsset(asset: PhysicalAsset, query?: string, paginator?: Paginator<Maintenance>): Promise<Maintenance[]> {
    return this.restApiManager.getAll<Maintenance>(
      Maintenance,
      `${environment.restApi.endpoint}/maintenances/search`,
      this.accountManager.account.authToken,
      {
        paginator,
        params: {
          // TODO add query param
          equipmentId: asset.id,
          companyId: this.accountManager.account.company.id
        }
      }
    );
  }

  // TODO avoid external maintenances
  public getLiveMaintenances(): BehaviorSubject<Maintenance[]> {

    if (this.liveMaintenances == null) {

      this.liveMaintenances = new BehaviorSubject<Maintenance[]>(null);

      // Start live maintenances feed
      const eventSource = new EventSource(`${environment.reactiveApi.endpoint}/companies/${this.accountManager.account.company.id}/maintenances`);

      eventSource.onmessage = async (event) => {
        const json = JSON.parse(event.data);

        // TODO BUG 'field_user_id', 'remote_user_id' e 'closed_by_user_id' sono null nella chiusura

        // Exclude if maintenance is incomplete
        if (!['field_user_id', 'event_id', 'equipment_id'].every(key => !!json[key])) {
          this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'An incomplete maintenance update has been received, skipping...', { maintenance: json['id'] });
          return;
        }

        // Create maintenance with required data
        const maintenance = new Maintenance();
        maintenance.id = json['id'];
        maintenance.createdAt = new Date(json['created_at']).getTime();
        maintenance.status = json['status'];
        maintenance.botDisconnectedAt = json['bot_disconnected_at'];
        maintenance.isBotEnabled = json['si_bot_enabled'];
        maintenance.closedAt = json['closed_at'];
        maintenance.closedBy = json['closed_by'];
        maintenance.isExtraordinary = json['is_extraordinary'];
        maintenance.isSupportRequired = json['is_support_required'];
        maintenance.reason = json['reason'];
        maintenance.scheduledAt = json['scheduled_at'];
        maintenance.takenInChargeAt = json['taken_in_charge_at'];

        // Get physical asset
        maintenance.equipment = await this.physicalAssetManagerService.getPhysicalAsset(json['equipment_id']);

        // Gather other required information
        const maintenanceData = await Promise.all([
          this.eventManagerService.getEvent(json['event_id']),
          this.personnelManagerService.getPersonnel(json['field_user_id']),
          json['remote_user_id'] ? await this.personnelManagerService.getPersonnel(json['remote_user_id']) : null,
          json['work_instruction_id'] ? await this.authoringManagerService.getProcedure(maintenance.equipment.model.id, json['work_instruction_id']) : null
        ]);
        maintenance.event = maintenanceData[0];
        maintenance.fieldWorker = maintenanceData[1];
        maintenance.remoteExpert = maintenanceData[2];
        maintenance.workInstruction = maintenanceData[3];


        // Add, update or remove maintenance
        if (
          maintenance.status == MaintenanceStatus.QUEUE ||
          maintenance.status == MaintenanceStatus.TAKEN ||
          maintenance.status == MaintenanceStatus.CLOSED ||
          maintenance.status == MaintenanceStatus.PLANNED
        ) {

          switch (maintenance.status) {

            case MaintenanceStatus.CLOSED:
              this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Removed maintenance with id ${maintenance.id}`);
              // Remove maintenance
              if (this.liveMaintenances.value.findIndex(x => x.id == maintenance.id) > -1) {
                const maintenances = [ ...this.liveMaintenances.value ];
                maintenances.splice(this.liveMaintenances.value.findIndex(x => x.id == maintenance.id), 1);
                this.processMaintenances(maintenances);
              }
              break;

            case MaintenanceStatus.QUEUE:
            case MaintenanceStatus.TAKEN:
            case MaintenanceStatus.PLANNED:
              if (!this.liveMaintenances.value.find(x => x.id == maintenance.id)) {
                this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Created maintenance with id ${maintenance.id}`);
                // Create if not exists
                this.processMaintenances([...this.liveMaintenances.value, maintenance]);
              } else {
                this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Updated maintenance with id ${maintenance.id}`);
                // Update maintenance
                this.processMaintenances([
                  ...this.liveMaintenances.value.map(x => {
                    if (x.id == maintenance.id) {
                      x.status = maintenance.status;
                      x.remoteExpert = maintenance.remoteExpert;
                    }
                    return x;
                  })
                ]);
              }
              break;

          }

        }

      }

      eventSource.onerror = (error) => {
        // console.error(error);
      }

      // Get current live maintenances
      this.fetchLiveMaintenances(new Paginator<Maintenance>({ ignorePagination: true, sorting: "DESC" }));

    }

    return this.liveMaintenances;

  }

  public async fetchLiveMaintenances(paginator?: Paginator<Maintenance>) {

    paginator = paginator || new Paginator<Maintenance>({ignorePagination: true});

    const maintenances = await this.restApiManager.getAll<Maintenance>(
      Maintenance,
      `${environment.restApi.endpoint}/maintenances/search`,
      this.accountManager.account.authToken,
      {paginator, params: {status: [1, 2, 5], companyId: this.accountManager.account.company.id}}
    );

    const filtered = maintenances.filter(maintenance =>
      maintenance.event.type != EventType.PLANNED_MAINTENANCE ||
      (maintenance.event.type == EventType.PLANNED_MAINTENANCE && maintenance.remoteExpert && maintenance.remoteExpert.id == this.accountManager.account.id)
    );

    this.processMaintenances(filtered);

  }

  private processMaintenances(maintenances: Maintenance[]) {

    // Notify only status 1 => queue
    const maintenancesToNotify = maintenances.filter(x => x.status == 1 && this.showedNotifications.indexOf(x.id) < 0);
    if (maintenancesToNotify.length == 1) {
      this.uiManager.notify(`${this.translatePipe.transform('New maintenance request from')} ${maintenancesToNotify[0].fieldWorker.name}`);
    } else if (maintenancesToNotify.length > 1) {
      this.uiManager.notify(this.translatePipe.transform('New maintenance requests'));
    }

    this.ngZone.run(() => {
      maintenancesToNotify.forEach(x => this.showedNotifications.push(x.id));
      this.liveMaintenances.next(maintenances);
    });

  }

  async getMaintenance(maintenanceId: string): Promise<Maintenance> {
    return this.restApiManager.get<Maintenance>(
      Maintenance,
      `${environment.restApi.endpoint}/maintenances/${maintenanceId}`,
      this.accountManager.account.authToken
    );
  }

  async removeMaintenance(maintenance: Maintenance): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}`,
      this.accountManager.account.authToken
    );
  }

  async updateMaintenance(maintenanceId: string, event: CalendarEvent): Promise<number> {
    return(await this.restApiManager.put<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${maintenanceId}`,
      {
        ...event.maintenance,
        equipmentId: event.maintenance.equipment.id,
        eventId: event.id,
        workInstructionId: event.maintenance.procedureId || event.maintenance.procedure?.id,
      },
      this.accountManager.account.authToken
    )).scheduledAt;
  }

  getStatus(status: number): MaintenanceStatus {
    switch (status) {
      case 0:
        return MaintenanceStatus.OPEN;
      case 1:
        return MaintenanceStatus.QUEUE;
      case 2:
        return MaintenanceStatus.TAKEN;
      case 3:
        return MaintenanceStatus.CLOSED;
      case 4:
        return MaintenanceStatus.SUSPENDED;
      case 5:
        return MaintenanceStatus.PLANNED;
      default:
        return MaintenanceStatus.UNKNOWN;
    }
  }

  async setMaintenanceStatus(maintenance: Maintenance, status: MaintenanceStatus): Promise<Maintenance> {
    return this.restApiManager.patch<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/status`,
      {
        status,
        remoteExpertId: maintenance.remoteExpert?.id,
        fieldWorkerId: maintenance.fieldWorker?.id,
        assignedById: maintenance.assignedBy?.id,
        isSupportRequired: maintenance.isSupportRequired
      },
      this.accountManager.account.authToken
    );
  }

  public async startSupportMaintenanceRequest(maintenance: Maintenance): Promise<Maintenance> {
    return this.restApiManager.patch<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/status`,
      {
        status: MaintenanceStatus.TAKEN,
        remoteExpertId: this.accountManager.account.id,
        fieldWorkerId: maintenance.fieldWorker.id,
        assignedById: maintenance.assignedBy?.id,
        isSupportRequired: maintenance.isSupportRequired
      },
      this.accountManager.account.authToken
    ).finally(() => {
      this.trackingManagerService.scheduleRecorder();
    });
  }

  public startVideocallRequest(assistance: Assistance, operatorId: string, pin: string) {
    this.sendVideoCallCard(assistance, operatorId, pin);
  }

  // @deprecated
  public async waitForDeviceToBeReady(assistance: Assistance, ignoreDeviceReady: boolean, callback: (deviceType: DeviceType, videocallProvider: VideocallProvider) => void) {

    const defaultVideocallProvider = VideocallProvider.JITSI;

    // Wait for next status update
    const eventSource = new EventSource(`${environment.reactiveApi.endpoint}/videocall/status`);

    eventSource.onmessage = (event) => {
      const status = VideocallStatus.fromFlux(JSON.parse(event.data));
      if (status.eventId.toString() == assistance.id && (ignoreDeviceReady || status.isDeviceReady)) {
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is ready. Starting...');
        callback(status.deviceType, status.videocallProvider || defaultVideocallProvider);
        eventSource.close();
      }
    };

    eventSource.onerror = (error) => {
      this.debugService.error(DEBUG.LIVE_ASSISTANCE, 'An error has occurred', error);
    };

    // And check current status
    try {

      await Utils.sleep(100);
      const status = await this.restApiManager.get<VideocallStatus>(
        VideocallStatus,
        `${environment.restApi.endpoint}/videocall_status/${assistance.id}`,
        this.accountManager.account.authToken
      );

      if (status.eventId.toString() == assistance.id && (ignoreDeviceReady || status.isDeviceReady)) {
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is ready. Starting...');
        callback(DeviceType[status.deviceType.toString()], status.videocallProvider || defaultVideocallProvider);
        eventSource.close();
      } else {
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is not ready yet. Waiting...');
      }

    } catch (ex) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is not ready yet. Waiting...');
    }

  }

  public async waitForDeviceToBeReadyFixed(assistance: Assistance, ignoreStatus: boolean): Promise<{ deviceType: DeviceType, videocallProvider: VideocallProvider }> {
    return new Promise(async (resolve, reject) => {

      const defaultVideocallProvider = VideocallProvider.JITSI;

      // Wait for next status update
      const eventSource = new EventSource(`${environment.reactiveApi.endpoint}/videocall/status`);

      eventSource.onmessage = (event) => {
        const status = VideocallStatus.fromFlux(JSON.parse(event.data));
        if (status.eventId.toString() == assistance.id && (ignoreStatus || status.isDeviceReady)) {
          this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is ready. Starting...');
          eventSource.close();
          resolve({
            deviceType: status.deviceType,
            videocallProvider: status.videocallProvider || defaultVideocallProvider
          });
        }
      };

      eventSource.onerror = (error) => {
        this.debugService.error(DEBUG.LIVE_ASSISTANCE, 'An error has occurred', error);
        eventSource.close();
        reject(error);
      };

      // And check current status
      try {

        await Utils.sleep(100);
        const status = await this.restApiManager.get<VideocallStatus>(
          VideocallStatus,
          `${environment.restApi.endpoint}/videocall_status/${assistance.id}`,
          this.accountManager.account.authToken
        );

        if (status.eventId.toString() == assistance.id && (ignoreStatus || status.isDeviceReady)) {
          this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is ready. Starting...');
          eventSource.close();
          resolve({
            deviceType: DeviceType[status.deviceType.toString()],
            videocallProvider: status.videocallProvider || defaultVideocallProvider
          });
        } else {
          this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is not ready yet. Waiting...');
        }

      } catch (ex) {
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Remote device is not ready yet. Waiting...');
      }

    });
  }

  public async closeMaintenance(assistance: Assistance) {

    // TODO migrate assistance.maintenanceId to assistance.id
    // Update maintenance status
    await this.restApiManager.patch<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${assistance.maintenance.id}/status`,
      {
        status: 3,
        remoteExpertId: this.accountManager.account.id,
        fieldWorkerId: assistance.fieldWorker.id,
        closedById: this.accountManager.account.id,
        isSupportRequired: assistance.maintenance.isSupportRequired,
        assignedById: assistance.maintenance.assignedBy?.id
      },
      this.accountManager.account.authToken
    );

    // Update maintenance status in realtime DB -> forces mobile to disconnect
    this.closeChat(assistance);

    // End associated event (maintenance)
    if (assistance.event.id) {
      return this.restApiManager.put<CalendarEvent>(
        `${environment.restApi.endpoint}/events/${assistance.event.id}`,
        {
          ...assistance.event,
          endAt: Date.now(),
          typeId: assistance.event.type,
          equipmentId: assistance.event.equipment.id,
          baseTrainingUrl: assistance.event.trainingUrl,
        },
        this.accountManager.account.authToken
      );
    }

    // Delete videocall branch in order to exclude other calls autostart
    switch (environment.authProvider) {
      case 'firebase':
      case 'keycloak':
        // TODO edit in chat module
        //this.realtimeDb.database.ref(`/videocall/ticket_${assistance.id}/`).remove();
        break;
      case 'parse':
        // TODO gestire parse
        break;
      default:
        throw new Error('Not Implemented');
    }

  }

  async closeExternalMaintenance(maintenance: Maintenance) {

    await this.restApiManager.patch<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/status`,
      {
        status: MaintenanceStatus.CLOSED,
      },
      this.accountManager.account.authToken
    );

  }

  public getBotDisconnectedAt(maintenanceId: string, messageId: string) {
    //this.realtimeDb.database.ref('messaging/tickets/ticket_' + maintenanceId + '/100/messages/').child(messageId).
  }

  /*public async isVideocallRequestAlreadySent(eventId: string): Promise<boolean> {
    const videocallRequest = await this.realtimeDb.database.ref('messaging/tickets/ticket_' + eventId + '/100/messages/!*!/card').once('value');
    return videocallRequest.exists();
  }*/

  getRecommendations(maintenance: Maintenance): Promise<MaintenanceRecommendations> {
    return this.restApiManager.get<MaintenanceRecommendations>(
      MaintenanceRecommendations,
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/recommendations`,
      this.accountManager.account.authToken
    );
  }

  async getFakeRecommendations(isSupportRequired: boolean): Promise<MaintenanceRecommendations> {
    const results = new MaintenanceRecommendations();
    results.remoteExperts = (await this.personnelManagerService.getAllPersonnel(new Paginator<Personnel>({ ignorePagination: true }))).map(expert => ({
      user: expert
    }) as MaintenanceRemoteExpertRecommendation);
    results.fieldWorkers = (await this.personnelManagerService.getAllPersonnel(new Paginator<Personnel>({ ignorePagination: true }))).map(fieldWorker => ({
      userSkill: { user: fieldWorker },
      hasRequiredLevel: !isSupportRequired
    }) as MaintenanceFieldWorkerRecommendation);
    return results;
  }

  async updatePersonnelBindings(maintenanceId: string, fieldWorkerId: string, remoteExpertId: string, assignedById: string, isSupportRequired: boolean) {
    await this.restApiManager.patch<Maintenance>(
      `${environment.restApi.endpoint}/maintenances/${maintenanceId}/status`,
      {
        status: 5,
        remoteExpertId: remoteExpertId,
        fieldWorkerId: fieldWorkerId,
        closedById: null,
        assignedById,
        isSupportRequired
      },
      this.accountManager.account.authToken
    );
  }

  async getMaintenanceProgress(maintenance: Maintenance): Promise<TimelineGroup[]> {

    // Check if we need to take all procedure or just a single task
    const allTasks: Task[] = maintenance.task
      ? [maintenance.task]
      : await this.authoringManagerService.getTasks(maintenance.procedure.id, new Paginator({ignorePagination: true, orderBy: 'sequence', sorting: 'ASC'}));

    const allSteps: Step[][] = await Promise.all(
      allTasks.map(async (task) => {
        return await this.authoringManagerService.getSteps(task.id, new Paginator({ignorePagination: true, orderBy: 'sequence', sorting: 'ASC'}));
      })
    );

    const maintenanceProgress = await this.restApiManager.getAll<MaintenanceProgress>(
      MaintenanceProgress,
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/step_executions`,
      this.accountManager.account.authToken
    );

    return allTasks.map((task, index) => {

      // Get steps
      const steps = allSteps[index];

      return {
        id: task.id,
        title: task.name,
        elements: steps.map(step => {

          // Check if step is executed or skipped
          const stepProgress = maintenanceProgress.find(x => x.taskId == step.task.id && x.stepId == step.id);

          return {
            id: step.id,
            status: stepProgress ? (stepProgress.isExecuted ? TimelineElementStatus.CHECKED : TimelineElementStatus.ERROR) : TimelineElementStatus.NONE,
            title: step.name,
            description: step.description,
            timestamp: stepProgress ? new Date(stepProgress.executedAt) : null
          } as TimelineElement;

        })
      } as TimelineGroup;

    });

  }

  setStepExecution(maintenanceId: string, procedureId: string, taskId: string, stepId: string, isExecuted: boolean) {
    return this.restApiManager.post<StepExecution>(
      `${environment.restApi.endpoint}/maintenances/${maintenanceId}/step_executions`,
      {
        workInstructionId: procedureId,
        taskId: taskId,
        stepId: stepId,
        isExecuted
      },
      this.accountManager.account.authToken
    );
  }

  async addTagsToMaintenance(maintenance: Maintenance, tags: string[]): Promise<void> {
    await this.restApiManager.post(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/tags`,
      { tags },
      this.accountManager.account.authToken
    );
  }

  async getTagsFromMaintenance(maintenance: Maintenance): Promise<string[]> {
    return (await this.restApiManager.get<{ tags: string[] }>(
      null,
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/tags`,
      this.accountManager.account.authToken,
    )).tags.filter(tag => tag != '');
  }

  getResources(maintenance: Maintenance, paginator?: Paginator<Resource>): Promise<Resource[]> {
    return this.restApiManager.getAll<Resource>(
      Resource,
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/resources`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  public setMaintenanceRecording(maintenance: Maintenance, bucket: string, objectName: string, contentType: string) {
    return this.restApiManager.patch<void>(
      `${environment.restApi.endpoint}/maintenances/${maintenance.id}/recording`,
      { bucket, objectName, contentType },
      this.accountManager.account.authToken
    );
  }

}
